/**
 * Episode of Care Type form — Zod contract for the Add/Edit modal in ServicesSettings.
 *
 * Enum array uses `satisfies` against the fhir-model type so compile breaks if
 * the server-side EocIntakeType changes.
 */
import { z } from 'zod';
import type { EocIntakeType } from '@unify/fhir-model/resources/healthcare-service';
import { EOC_TYPE_SLUG_PATTERN } from '@/pages/calendar/episodeOfCareTypeCatalog';

export const eocIntakeTypeValues = [
  'both',
  'consultations',
  'intake-sessions',
] as const satisfies readonly EocIntakeType[];

export const episodeOfCareTypeSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .regex(EOC_TYPE_SLUG_PATTERN, 'Lowercase letters, numbers, and hyphens only'),
  intakeType: z.enum(eocIntakeTypeValues),
  intakeSessionTypeId: z.string().nullable(),
  consultationSessionTypeId: z.string().nullable(),
  standardSessionTypeId: z.string().nullable(),
  /** Additional approved session types (extras; intake/consultation/standard are implicitly approved). */
  approvedSessionTypeIds: z.array(z.string()),
  active: z.boolean(),
  /**
   * Sohar specialty code — exactly 3 letters, required.
   * Transformed to uppercase on parse so submit always produces an uppercase value.
   */
  soharSpecialtyCode: z
    .string()
    .regex(/^[A-Za-z]{3}$/, 'Enter exactly 3 letters')
    .transform((v) => v.toUpperCase()),
});

export type EpisodeOfCareTypeFormValues = z.infer<typeof episodeOfCareTypeSchema>;

export const EMPTY_EOC_TYPE_FORM: EpisodeOfCareTypeFormValues = {
  name: '',
  code: '',
  intakeType: 'both',
  intakeSessionTypeId: null,
  consultationSessionTypeId: null,
  standardSessionTypeId: null,
  approvedSessionTypeIds: [],
  active: false,
  soharSpecialtyCode: '',
};
